import JSZip from 'jszip';
import jsPDF from 'jspdf';
import logo from '../.././images/logo337.png';
import { parse, format } from 'date-fns';
import "./DownloadModal.scss";

/** Mapeo opcional de documentTypeId a nombre de carpeta (legacy). Tipos no listados usan el id sanitizado. */
const FOLDER_NAME_MAP = {
    legal: 'documentos-legales',
    ims: 'documentos-ims',
    '112': 'documentos-112',
    denuncia: 'documentos-denuncias',
    intervencion: 'documentos-intervenciones',
    'parte de trabajo': 'documentos-partes-trabajo'
};

/**
 * Nombre de carpeta para exportación. Dinámico: si el tipo no está mapeado, se usa el documentTypeId sanitizado.
 * @param {string} documentType - documentTypeId del documento (puede ser cualquier valor según el formulario)
 */
export function getFolderName(documentType) {
    if (!documentType) return 'documentos';
    const mapped = FOLDER_NAME_MAP[documentType];
    if (mapped) return mapped;
    return 'documentos-' + String(documentType).replace(/\s+/g, '-').replace(/[^a-z0-9-_]/gi, '-').replace(/-+/g, '-').toLowerCase();
}

/**
* Descarga las fotos seleccionadas de los documentos.
* @param {Array} documents - Documentos seleccionados.
* @param {Object} folder - Carpeta donde se guardarán las fotos en el ZIP.
*/
export async function downloadSelectedPhotos(documents, folder) {
    for (const document of documents) {
        const urls = document.fields ? collectAllPhotoUrlsFromFields(document.fields) : [];
        if (urls.length > 0) {
            const folderName = getFolderName(document.documentTypeId);
            const folderRef = folder.folder(folderName);
            const formattedDate = formatDateForFolder(document.fecha);
            const results = await Promise.all(urls.map(async (url, i) => {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    return { i, blob };
                } catch (err) {
                    console.warn('Error al descargar foto:', url, err);
                    return { i, blob: null };
                }
            }));
            results.forEach(({ i, blob }) => {
                if (blob) {
                    const photoFileName = `${formattedDate}-${document.tienda}-${document.id}-foto-${i}.jpg`;
                    folderRef.file(photoFileName, blob);
                }
            });
        }
    }
}

/**
 * Formatea la fecha de "dd/mm/aaaa" a "dd-mm-aaaa".
 * Acepta string, Date o objeto Firestore (seconds/nanoseconds).
 * @param {string|Date|Object} dateString - Fecha en formato "dd/mm/aaaa", Date o timestamp.
 * @returns {string} - Fecha formateada en "dd-mm-aaaa".
 */
export function formatDateForFolder(dateString) {
    if (dateString == null || dateString === '') return '';

    let str = dateString;
    if (typeof dateString !== 'string') {
        let dateObj;
        if (dateString instanceof Date) {
            dateObj = dateString;
        } else if (typeof dateString.toDate === 'function') {
            dateObj = dateString.toDate();
        } else if (typeof dateString.seconds === 'number') {
            dateObj = new Date(dateString.seconds * 1000 + (dateString.nanoseconds || 0) / 1000000);
        } else if (typeof dateString === 'number') {
            dateObj = new Date(dateString);
        } else {
            return String(dateString);
        }
        if (Number.isNaN(dateObj.getTime())) return '';
        const d = dateObj.getDate();
        const m = dateObj.getMonth() + 1;
        const y = dateObj.getFullYear();
        str = `${d < 10 ? '0' : ''}${d}/${m < 10 ? '0' : ''}${m}/${y}`;
    }

    // Si la fecha contiene hora (formato "dd/mm/aaaa, hh:mm:ss"), extraer solo la parte de la fecha
    let dateOnly = str;
    if (str.includes(',')) {
        dateOnly = str.split(',')[0].trim();
    }

    const parts = dateOnly.split('/');
    if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        return `${day}-${month}-${year}`;
    }
    return dateOnly;
}

/** Convierte hex (#FFBF00 o FFBF00) a [r, g, b] para jsPDF. */
function hexToRgb(hex) {
    if (!hex || typeof hex !== 'string') return [255, 191, 0];
    const h = hex.replace(/^#/, '');
    if (h.length !== 6) return [255, 191, 0];
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return [r, g, b];
}

/**
 * @param {Array} documents - Documentos a exportar.
 * @param {Object} folder - Carpeta ZIP donde guardar.
 * @param {string} exportType - 'pdf' o undefined.
 * @param {(doc: Object) => Promise<{ sections?, headerFields?, logoUrl?, primaryColor?, accentColor? }|null>} [getPdfDesign] - Opcional: obtiene el diseño PDF para un documento.
 * @param {Object} [formDefinition] - Opcional: definición del formulario con inputs y su orden.
 */
export async function downloadSelectedDocumentsAsPDF(documents, folder, exportType, getPdfDesign, formDefinition = null) {

    const defaultLogoPath = logo;
    const logoWidth = 170;
    const logoHeight = 70;

    for (const document of documents) {
        // Generar PDF si se pide 'pdf' o si no se indica tipo (llamada legacy sin tercer argumento)
        if (exportType === 'pdf' || exportType === undefined) {
            let design = null;
            try {
                design = typeof getPdfDesign === 'function' ? await getPdfDesign(document) : null;
            } catch (e) {
                console.warn('Error al obtener diseño PDF:', e);
            }
            const primaryRgb = hexToRgb(design?.primaryColor);
            const accentRgb = hexToRgb(design?.accentColor);

            const resolveLogo = async (url) => {
                if (!url || !url.startsWith('http')) return null;
                try {
                    const res = await fetch(url);
                    const blob = await res.blob();
                    return { blobUrl: URL.createObjectURL(blob), format: blob.type && blob.type.includes('jpeg') ? 'JPEG' : 'PNG' };
                } catch (e) {
                    console.warn('Error al cargar logo:', e);
                    return null;
                }
            };
            const defaultLogoBlob = await fetch(defaultLogoPath).then((r) => r.blob());
            const defaultLogoUrl = URL.createObjectURL(defaultLogoBlob);

            const leftLogoUrl = design?.logoLeftUrl?.trim() || null;
            const rightLogoUrl = (design?.logoRightUrl?.trim() || design?.logoUrl?.trim() || null);
            const leftLogo = leftLogoUrl ? await resolveLogo(leftLogoUrl) : null;
            const rightLogo = rightLogoUrl ? await resolveLogo(rightLogoUrl) : null;

            const pdf = new jsPDF('p', 'pt', 'letter', true);

            const margin = 20;
            const pageWidth = pdf.internal.pageSize.getWidth();
            const contentWidth = pageWidth - 2 * margin;
            const contentHeight = pdf.internal.pageSize.getHeight() - 2 * margin;
            const logoPositionY = margin;

            if (leftLogo?.blobUrl) {
                pdf.addImage(leftLogo.blobUrl, leftLogo.format, margin, logoPositionY, logoWidth, logoHeight);
            }
            if (rightLogo?.blobUrl) {
                pdf.addImage(rightLogo.blobUrl, rightLogo.format, pageWidth - margin - logoWidth, logoPositionY, logoWidth, logoHeight);
            }
            if (!leftLogo?.blobUrl && !rightLogo?.blobUrl) {
                pdf.addImage(defaultLogoUrl, 'PNG', pageWidth - margin - logoWidth, logoPositionY, logoWidth, logoHeight);
            }

            const addHeaderLogos = () => {
                if (leftLogo?.blobUrl) pdf.addImage(leftLogo.blobUrl, leftLogo.format, margin, logoPositionY, logoWidth, logoHeight);
                if (rightLogo?.blobUrl) pdf.addImage(rightLogo.blobUrl, rightLogo.format, pageWidth - margin - logoWidth, logoPositionY, logoWidth, logoHeight);
                if (!leftLogo?.blobUrl && !rightLogo?.blobUrl) pdf.addImage(defaultLogoUrl, 'PNG', pageWidth - margin - logoWidth, logoPositionY, logoWidth, logoHeight);
            };

            pdf.setFontSize(24);
            pdf.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
            pdf.setFont('helvetica', 'bold');
            const documentName = (document.documentTypeName || document.documentTypeId || 'Documento').toString();
            const documentNameWidth = pdf.getStringUnitWidth(documentName) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
            const documentNameX = pdf.internal.pageSize.getWidth() / 2 - documentNameWidth / 2;
            const documentNameY = margin + logoHeight + 50;
            pdf.text(documentName, documentNameX, documentNameY);

            // Estilos para el contenido de texto
            const smallerFontSize = 12;
            pdf.setFontSize(smallerFontSize);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');

            // Dibujar las líneas de cabecera (configurables desde el diseño o por defecto)
            const defaultHeaderFields = [
                { label: 'Vigilante', documentKey: 'vigilante' },
                { label: 'Servicio', documentKey: 'tienda' },
                { label: 'Cliente', documentKey: 'cliente' },
                { label: 'Fecha de subida', documentKey: 'fecha' }
            ];
            const headerFieldsToUse = (design?.headerFields?.length > 0) ? design.headerFields : defaultHeaderFields;
            let textY = margin + 25;
            
            // Función helper para buscar valor en document y document.fields
            // Soporta el nuevo formato indexado: { index, name, type, value }
            const getFieldValue = (doc, key) => {
                if (!key) return '';
                
                // Helper para extraer valor de objeto indexado
                const extractValue = (v) => {
                    if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'index' in v) {
                        return v.value;
                    }
                    return v;
                };
                
                // Buscar primero en el documento directamente
                if (doc[key] != null) return extractValue(doc[key]);
                // Buscar con primera letra mayúscula
                const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
                if (doc[capitalizedKey] != null) return extractValue(doc[capitalizedKey]);
                // Buscar en document.fields
                if (doc.fields) {
                    if (doc.fields[key] != null) return extractValue(doc.fields[key]);
                    if (doc.fields[capitalizedKey] != null) return extractValue(doc.fields[capitalizedKey]);
                    // Buscar case-insensitive en fields
                    const fieldsKeys = Object.keys(doc.fields);
                    const matchingKey = fieldsKeys.find(k => k.toLowerCase() === key.toLowerCase());
                    if (matchingKey && doc.fields[matchingKey] != null) return extractValue(doc.fields[matchingKey]);
                }
                return '';
            };
            
            for (const field of headerFieldsToUse) {
                const label = (field.label || field.documentKey || '').trim() || field.documentKey;
                if (!label && !field.documentKey) continue;
                let value = getFieldValue(document, field.documentKey);
                if (value == null) value = '';
                // Si es un objeto (como Timestamp), intentar formatearlo
                if (typeof value === 'object' && value !== null) {
                    if (typeof value.toDate === 'function') {
                        try {
                            // Para el campo fecha del header, no mostrar hora
                            const isDateField = field.documentKey?.toLowerCase() === 'fecha';
                            if (isDateField) {
                                value = value.toDate().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
                            } else {
                                value = value.toDate().toLocaleDateString('es-ES');
                            }
                        } catch { value = String(value); }
                    } else {
                        value = String(value);
                    }
                } else if (typeof value === 'string') {
                    // Formatear fecha ISO sin hora para el campo fecha
                    const isDateField = field.documentKey?.toLowerCase() === 'fecha';
                    if (isDateField) {
                        // Detectar formato ISO (yyyy-mm-dd o yyyy-mm-ddThh:mm)
                        const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (isoMatch) {
                            const [, year, month, day] = isoMatch;
                            value = `${day}/${month}/${year}`;
                        }
                    }
                } else {
                    value = String(value);
                }
                if (field.documentKey === 'vigilante') value = value.replace('@gmail.com', '');
                pdf.text(`${label}: ${value}`, margin, textY + documentNameY);
                textY += 20;
            }

            // Espacio claro entre cuerpo (Vigilante, Servicio, Fecha) y la primera sección de inputs
            const bodyBottomY = textY + documentNameY;
            const gapBodyToSections = 50;
            textY = bodyBottomY + gapBodyToSections;

            // Contenido dinámico: agrupar por sección y dibujar encabezados visibles
            pdf.setFont('helvetica', 'normal');
            let flattened = [];
            try {
                flattened = flattenFieldsForPdf(document.fields || {}, '', '', formDefinition);
            } catch (e) {
                console.warn('Error al aplanar campos para PDF:', e);
            }
            const options = { width: contentWidth, align: 'left' };
            const lineHeight = 14;
            const sectionGap = 8;
            const pageHeight = pdf.internal.pageSize.getHeight();
            const maxY = pageHeight - margin - 60;

            if (flattened.length > 0) {
                const sections = design?.sections?.length ? groupFlattenedByDesign(flattened, design) : groupFlattenedBySection(flattened);
                for (const { sectionName, entries } of sections) {
                    // Encabezado de sección (negrita + línea)
                    if (textY + 40 > maxY) {
                        pdf.addPage();
                        textY = margin + 20;
                    }
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(13);
                    pdf.setTextColor(0, 0, 0);
                    pdf.text(sectionName, margin, textY);
                    textY += 18;
                    pdf.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
                    pdf.line(margin, textY, margin + contentWidth, textY);
                    textY += sectionGap + 10; // Espacio entre línea y texto inferior
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(12);

                    for (const { label, value } of entries) {
                        // Eliminar el prefijo de sección del label para evitar redundancia
                        let displayLabel = label;
                        const separatorIndex = label.indexOf(' | ');
                        if (separatorIndex !== -1) {
                            displayLabel = label.slice(separatorIndex + 3); // Después del " | "
                        } else if (label.startsWith(sectionName + ' > ')) {
                            displayLabel = label.slice((sectionName + ' > ').length);
                        }
                        const fieldText = `${displayLabel}: ${value}`;
                        const lines = pdf.splitTextToSize(fieldText, contentWidth);
                        if (textY + lines.length * lineHeight > maxY) {
                            pdf.addPage();
                            textY = margin + 20;
                        }
                        pdf.text(lines, margin, textY, options);
                        textY += lines.length * lineHeight + 4;
                    }
                    textY += sectionGap + 6;
                }
            }


            // Agregar footer (color del diseño o amarillo por defecto)
            const footerHeight = 10;
            pdf.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
            pdf.rect(margin, pdf.internal.pageSize.getHeight() - margin - footerHeight, contentWidth, footerHeight, 'F');

            // Configurar color de texto en blanco
            pdf.setTextColor('#FFFFFF'); // Blanco

            pdf.setFontSize(10);
            // pdf.text(footerText, margin + 10, pdf.internal.pageSize.getHeight() - margin - footerHeight + 15);

            // Después de agregar el contenido principal del PDF pero antes de guardar el PDF
            // Agregar una nueva página
            pdf.addPage();

            addHeaderLogos();
            // Configurar color de texto en blanco para la nueva página
            pdf.setTextColor('#000000'); // Negro

            
            // Fotos: desde fields.fotos o desde secciones condicionales (ej. Añadir fotografías_cond_0)
            let photoUrls = [];
            try {
                photoUrls = collectAllPhotoUrlsFromFields(document.fields || {});
            } catch (e) {
                console.warn('Error al recoger fotos para PDF:', e);
            }
            if (photoUrls.length > 0) {
                const fotoHeight = 250;
                const fotoWidth = 250;
                let fotoX = 50;
                let fotoY = 100;
                for (const fotoUrl of photoUrls) {
                    try {
                        const fotoData = await fetch(fotoUrl);
                        const fotoBlob = await fotoData.blob();
                        const fotoUrlForPdf = URL.createObjectURL(fotoBlob);
                        if (fotoX + fotoWidth > contentWidth + margin) {
                            fotoX = margin;
                            fotoY += fotoHeight + 10;
                        }
                        pdf.addImage(fotoUrlForPdf, 'JPEG', fotoX, fotoY, fotoWidth, fotoHeight);
                        fotoX += fotoWidth + 10;
                        URL.revokeObjectURL(fotoUrlForPdf);
                    } catch (err) {
                        console.warn('Error al cargar foto para PDF:', fotoUrl, err);
                    }
                }
            }

            // SEGUNDA PÁGINA: Firmas
            pdf.addPage();
            addHeaderLogos();

            let currentY = logoPositionY + logoHeight + 50;

            // Título de la sección de firmas
            pdf.setFontSize(16);
            pdf.setTextColor('#000000');
            pdf.setFont('helvetica', 'bold');
            pdf.text('Firmas del documento', margin, currentY);
            currentY += 40;

            // Procesar firmas (incluye firmas en campos anidados/condicionales)
            let signatureEntries = [];
            try {
                signatureEntries = collectSignatureEntriesForPdf(document.fields || {});
            } catch (e) {
                console.warn('Error al recoger firmas para PDF:', e);
            }
            for (const { label, url } of signatureEntries) {
                try {
                    const signatureResponse = await fetch(url);
                    const signatureBlob = await signatureResponse.blob();
                    const signatureUrl = URL.createObjectURL(signatureBlob);

                    // Título del campo de firma
                    pdf.setFontSize(14);
                    pdf.setTextColor('#000000');
                    pdf.setFont('helvetica', 'bold');
                    pdf.text(label, margin, currentY);
                    currentY += 25;

                    // Agregar la firma
                    const signatureWidth = 200;
                    const signatureHeight = 100;
                    pdf.addImage(signatureUrl, 'PNG', margin, currentY, signatureWidth, signatureHeight);
                    currentY += signatureHeight + 40;

                    URL.revokeObjectURL(signatureUrl);
                } catch (error) {
                    console.error('Error al procesar la firma:', error);
                }
            }

            // Agregar texto al nueva página
            const footerText = `
                                                                      CLAUSULA LEGAL

    La información contenida en este documento es confidencial y está destinada a ser leída solo por la(s) persona(s)
    a la(s) que va dirigida. Si usted lee este documento y no es su destinatario o no está expresamente autorizado
    para leerlo, le informamos que pueda ser ilegal cualquier divulgación, distribución o reproducción del mismo,
    por lo que le instamos a que nos lo notifique inmediatamente y proceda a su destrucción.
    Recuerde en todo caso que, quienes intervengan en cualquier fase del tratamiento de datos de carácter personal
    están obligados al secreto profesional respecto de los mismos y al deber de guardarlos, obligaciones que subsistirán
    aún después de finalizar sus relaciones con el titular del fichero de datos o, en su caso, con el responsable del
    mismo (Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos 
    digitales).                  
`;
            pdf.setFontSize(10);
            pdf.text(footerText, margin + 10, margin + 600); // Ajusta las coordenadas según tu diseño

            pdf.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
            pdf.rect(margin, pdf.internal.pageSize.getHeight() - margin - footerHeight, contentWidth, footerHeight, 'F');

            const pdfBytes = pdf.output('arraybuffer');
            if (leftLogo?.blobUrl) try { URL.revokeObjectURL(leftLogo.blobUrl); } catch (_) {}
            if (rightLogo?.blobUrl) try { URL.revokeObjectURL(rightLogo.blobUrl); } catch (_) {}
            if (defaultLogoUrl) try { URL.revokeObjectURL(defaultLogoUrl); } catch (_) {}

            const documentTypeFolder = folder.folder(getFolderName(document.documentTypeId));
            const formattedDate = formatDateForFilename(document.fecha) || document.id || 'documento';

            documentTypeFolder.file(`${formattedDate}-${(document.tienda || '').replace(/[/\\?*:|"]/g, '-')}-${document.id}.pdf`, pdfBytes);
        }
    }
}

/**
 * Formatea la fecha del documento para el nombre del archivo PDF (sin depender de moment).
 * Acepta string (ej. "29/1/2026, 16:38:33"), Date, o Firestore Timestamp.
 */
function formatDateForFilename(fecha) {
    if (fecha == null) return '';
    if (typeof fecha === 'string') return formatDateForFolder(fecha);
    try {
        const d = typeof fecha.toDate === 'function' ? fecha.toDate() : new Date(fecha);
        if (isNaN(d.getTime())) return '';
        const day = d.getDate();
        const month = d.getMonth() + 1;
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    } catch {
        return '';
    }
}



export function capitalizeFirstLetter(string) {
    const s = string != null ? String(string) : '';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/** True si el valor es Firestore Timestamp o Date; no debe recorrerlo como objeto. */
function isTimestampOrDate(value) {
    if (value == null) return false;
    if (value instanceof Date) return true;
    return typeof value === 'object' && typeof value.toDate === 'function';
}

/**
 * Returns true if the value is a local file URL from mobile device (should be ignored)
 */
function isLocalFileUrl(value) {
    if (typeof value !== 'string') return false;
    return value.startsWith('file:///') || value.includes('/cache/ImagePicker/');
}

/**
 * Returns true if the value looks like a signature or photo URL (blob/data/storage), not a normal string to display.
 * Esos valores se muestran en las secciones de fotos/firmas, no en el cuerpo del PDF.
 */
function isSpecialUrl(value) {
    if (typeof value !== 'string') return false;
    return (
        value.includes('signature') ||
        value.startsWith('blob:') ||
        value.startsWith('data:') ||
        value.includes('firebasestorage.googleapis.com') ||
        isLocalFileUrl(value) // Ignorar URLs locales de móvil
    );
}

/**
 * Returns true if the string looks like a VALID image URL (photo or signature).
 * Solo URLs de Firebase Storage o URLs web válidas, NO URLs locales de móvil.
 */
function isImageOrSignatureUrl(value) {
    if (typeof value !== 'string') return false;
    // Ignorar URLs locales de móvil - no son accesibles desde web
    if (isLocalFileUrl(value)) return false;
    
    const v = value.toLowerCase();
    return (
        v.includes('firebasestorage.googleapis.com') ||
        v.includes('firebase') && (v.includes('/fotos/') || v.includes('/photos/') || v.includes('photo')) ||
        v.includes('signatures') ||
        /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(value) ||
        value.startsWith('blob:') ||
        value.startsWith('data:')
    );
}

/** True si la URL es de firma (no debe contarse como foto). */
function isSignatureUrl(value) {
    if (typeof value !== 'string') return false;
    return value.toLowerCase().includes('signature');
}

/** True si la clave parece ser de una foto (foto_0, Fotografías_0, photo_1, image_2, etc.). Coincide con el form UPLOAD "Fotografías". */
function isPhotoKey(key) {
    return /^(foto|photo|image|fotografías)_?\d*$/i.test(String(key));
}

/** Ordena entradas [key, value] por número de foto (foto_0, foto_1, ...). */
function sortPhotoEntries(entries) {
    return entries.slice().sort((a, b) => {
        const numA = parseInt((a[0].match(/\d+/) || [0])[0], 10);
        const numB = parseInt((b[0].match(/\d+/) || [0])[0], 10);
        return (numA || 0) - (numB || 0);
    });
}

/**
 * Recursively collects all photo URLs from document.fields.
 * Incluye secciones condicionales (claves *_cond_0, *_cond_1...) y cualquier objeto/array anidado.
 * Ordena fotos por clave numérica (foto_0, foto_1, ...) cuando aplica.
 * @param {Object} fields - document.fields (puede ser el map completo o un sub-objeto)
 * @returns {string[]} - Array de URLs de fotos en orden
 */
export function collectAllPhotoUrlsFromFields(fields) {
    if (!fields || typeof fields !== 'object') return [];
    const urls = [];

    function addPhotoUrl(url) {
        if (typeof url !== 'string') return;
        if (isSignatureUrl(url)) return;
        if (!isImageOrSignatureUrl(url)) return;
        urls.push(url);
    }

    function walk(obj) {
        if (!obj || typeof obj !== 'object') return;
        if (Array.isArray(obj)) {
            obj.forEach((item) => walk(item));
            return;
        }
        const entries = Object.entries(obj);
        const photoEntries = entries.filter(([k, v]) => isPhotoKey(k) && typeof v === 'string');
        const restEntries = entries.filter(([k, v]) => !isPhotoKey(k));

        if (photoEntries.length > 0) {
            const sorted = sortPhotoEntries(photoEntries);
            sorted.forEach(([, val]) => addPhotoUrl(val));
        }
        restEntries.forEach(([key, value]) => {
            if (typeof value === 'string') {
                addPhotoUrl(value);
            } else if (value !== null && typeof value === 'object' && !isTimestampOrDate(value)) {
                if (Array.isArray(value)) {
                    value.forEach((item) => walk(item));
                } else {
                    walk(value);
                }
            }
        });
    }

    walk(fields);
    return urls;
}

/**
 * Flattens document.fields for PDF/export, including nested objects and arrays (e.g. condicionales, VS).
 * Uses humanized labels for keys like "Añadir fotografías_cond_0" and expands array items (VS 1, VS 2...).
 * @param {Object} fields - document.fields (may contain nested objects and arrays)
 * @param {string} prefix - Optional label prefix for nested keys
 * @returns {Array<{ label: string, value: string }>}
 */
/**
 * Separador usado para indicar jerarquía en etiquetas de campos.
 * Se usa " | " para separar sección de campo (ej: "VS 1 | Nº TIP")
 */
const SECTION_SEPARATOR = ' | ';

/**
 * Detecta si una clave es un campo condicional (ej: "vsCA_cond_0", "Añadir fotografías_cond_0")
 */
function isConditionalKey(key) {
    return /_cond_?\d*$/i.test(key) || /_cond_nested/i.test(key);
}

/**
 * Obtiene la clave padre de un campo condicional
 * "vsCA_cond_0" -> "vsCA"
 */
function getParentKeyFromConditional(key) {
    return key.replace(/_cond.*$/i, '');
}

/**
 * Obtiene el orden de los campos desde la definición del formulario
 * @param {Object} formDefinition - Definición del formulario con inputs
 * @returns {Object} - Mapa de nombre de campo -> orden
 */
function getFieldOrderFromForm(formDefinition) {
    if (!formDefinition?.inputs) return {};
    const orderMap = {};
    
    for (const [key, input] of Object.entries(formDefinition.inputs)) {
        if (input.order != null) {
            orderMap[key] = input.order;
            orderMap[input.name] = input.order;
            orderMap[input.title] = input.order;
        }
    }
    
    return orderMap;
}

/**
 * Obtiene el orden de los campos dentro de una sección repetible
 * @param {Object} formDefinition - Definición del formulario
 * @param {string} sectionName - Nombre de la sección (ej: "VS")
 * @returns {Array<string>} - Lista de nombres de campos en orden
 */
function getSectionFieldsOrder(formDefinition, sectionName) {
    if (!formDefinition?.inputs) return [];
    
    // Buscar la sección repetible
    for (const [key, input] of Object.entries(formDefinition.inputs)) {
        if ((input.name === sectionName || key === sectionName) && input.type === 'REPEATABLE_SECTION') {
            if (Array.isArray(input.sectionFields)) {
                // Crear lista ordenada de nombres de campos
                const orderedFields = [];
                for (const field of input.sectionFields) {
                    orderedFields.push(field.name || field.title);
                    // Si tiene campos condicionales, añadirlos después
                    if (field.conditionalFields) {
                        for (const [condKey, condFields] of Object.entries(field.conditionalFields)) {
                            if (Array.isArray(condFields)) {
                                for (const cf of condFields) {
                                    orderedFields.push(cf.name || cf.title);
                                }
                            }
                        }
                    }
                }
                return orderedFields;
            }
        }
    }
    return [];
}

/**
 * Obtiene el nombre del campo condicional desde la definición del formulario
 * @param {Object} formDefinition - Definición del formulario
 * @param {string} sectionName - Nombre de la sección (ej: "VS")
 * @param {string} parentFieldName - Nombre del campo padre (ej: "vsCA")
 * @param {number} condIndex - Índice del condicional (ej: 0)
 * @returns {string|null} - Nombre del campo condicional o null
 */
function getConditionalFieldName(formDefinition, sectionName, parentFieldName, condIndex) {
    if (!formDefinition?.inputs) return null;
    
    const baseSectionName = sectionName ? sectionName.replace(/\s+\d+$/, '') : '';
    
    // Buscar en sección repetible
    for (const [key, input] of Object.entries(formDefinition.inputs)) {
        if ((input.name === baseSectionName || key === baseSectionName) && input.type === 'REPEATABLE_SECTION') {
            if (Array.isArray(input.sectionFields)) {
                for (const field of input.sectionFields) {
                    if ((field.name === parentFieldName || field.title === parentFieldName) && field.conditionalFields) {
                        const condFields = field.conditionalFields[String(condIndex)];
                        if (Array.isArray(condFields) && condFields.length > 0) {
                            return condFields[0].name || condFields[0].title;
                        }
                    }
                }
            }
        }
        // Buscar en campos de nivel superior
        if ((input.name === parentFieldName || key === parentFieldName) && input.conditionalFields) {
            const condFields = input.conditionalFields[String(condIndex)];
            if (Array.isArray(condFields) && condFields.length > 0) {
                return condFields[0].name || condFields[0].title;
            }
        }
    }
    return null;
}

/**
 * Ordena las claves de un objeto según el orden del formulario o condicionales
 */
function sortFieldKeys(keys, formDefinition, sectionName) {
    // Separar claves normales y condicionales
    const normalKeys = [];
    const conditionalKeys = [];
    
    for (const key of keys) {
        if (isConditionalKey(key)) {
            conditionalKeys.push(key);
        } else {
            normalKeys.push(key);
        }
    }
    
    // Si tenemos definición de sección repetible, usar ese orden para las claves normales
    let orderedNormalKeys = normalKeys;
    if (sectionName && formDefinition) {
        const baseSectionName = sectionName.replace(/\s+\d+$/, '');
        const sectionOrder = getSectionFieldsOrder(formDefinition, baseSectionName);
        
        if (sectionOrder.length > 0) {
            const ordered = [];
            const used = new Set();
            
            for (const fieldName of sectionOrder) {
                for (const key of normalKeys) {
                    if (key === fieldName || key.toLowerCase() === fieldName.toLowerCase()) {
                        if (!used.has(key)) {
                            ordered.push(key);
                            used.add(key);
                        }
                    }
                }
            }
            
            // Añadir claves normales restantes
            for (const key of normalKeys) {
                if (!used.has(key)) {
                    ordered.push(key);
                }
            }
            
            orderedNormalKeys = ordered;
        }
    }
    
    // Construir resultado: cada clave normal seguida de sus condicionales
    const result = [];
    for (const key of orderedNormalKeys) {
        result.push(key);
        // Buscar condicionales que pertenecen a esta clave
        const relatedConditionals = conditionalKeys.filter(ck => getParentKeyFromConditional(ck) === key);
        // Ordenar condicionales por índice
        relatedConditionals.sort((a, b) => {
            const idxA = parseInt(a.match(/_cond_?(\d+)/)?.[1] || '0');
            const idxB = parseInt(b.match(/_cond_?(\d+)/)?.[1] || '0');
            return idxA - idxB;
        });
        result.push(...relatedConditionals);
    }
    
    // Añadir condicionales huérfanos al final
    const usedConditionals = new Set(result.filter(k => isConditionalKey(k)));
    for (const ck of conditionalKeys) {
        if (!usedConditionals.has(ck)) {
            result.push(ck);
        }
    }
    
    return result;
}

/**
 * Detecta si un valor tiene el nuevo formato con index: { index, name, type, value, nestedFields? }
 */
function isIndexedField(value) {
    return value && typeof value === 'object' && 
           !Array.isArray(value) && 
           'index' in value && 
           'value' in value &&
           !isTimestampOrDate(value);
}

/**
 * Extrae el valor real de un campo, ya sea directo o del nuevo formato indexado
 */
function extractFieldValue(value) {
    if (isIndexedField(value)) {
        return value.value;
    }
    return value;
}

/**
 * Obtiene el índice de un campo indexado, o Infinity si no tiene
 */
function getFieldIndex(value) {
    if (isIndexedField(value)) {
        return value.index ?? Infinity;
    }
    return Infinity;
}

/**
 * Formatea un valor de hora (ej: "2026-02-04T03:11" -> "04/02/2026 03:11")
 */
function formatTimeValue(value) {
    if (!value || typeof value !== 'string') return String(value);
    // Detectar formato ISO datetime (yyyy-mm-ddThh:mm)
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (isoMatch) {
        const [, year, month, day, hour, minute] = isoMatch;
        return `${day}/${month}/${year} ${hour}:${minute}`;
    }
    return String(value);
}

/**
 * Formatea un valor de fecha sin hora (ej: "2026-02-11" -> "11/02/2026")
 */
function formatDateOnly(value) {
    if (!value || typeof value !== 'string') return String(value);
    // Detectar formato ISO date (yyyy-mm-dd)
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${day}/${month}/${year}`;
    }
    return String(value);
}

/**
 * Detecta si un objeto fields usa el nuevo formato indexado
 * (al menos un campo tiene {index, value})
 */
function hasIndexedFormat(fields) {
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return false;
    return Object.values(fields).some(v => isIndexedField(v));
}

export function flattenFieldsForPdf(fields, prefix = '', sectionName = '', formDefinition = null) {
    if (!fields || typeof fields !== 'object') return [];
    const result = [];
    
    if (Array.isArray(fields)) {
        fields.forEach((item, index) => {
            if (item != null && typeof item === 'object' && !Array.isArray(item)) {
                // Para arrays (como VS), cada elemento es una sección separada
                const newSectionName = prefix ? `${prefix} ${index + 1}` : `${index + 1}`;
                result.push(...flattenFieldsForPdf(item, '', newSectionName, formDefinition));
            }
        });
        return result;
    }
    
    // Detectar si usa el nuevo formato indexado
    const useIndexedFormat = hasIndexedFormat(fields);
    
    let sortedKeys;
    if (useIndexedFormat) {
        // NUEVO FORMATO: Ordenar por index
        const entries = Object.entries(fields);
        entries.sort((a, b) => {
            const indexA = getFieldIndex(a[1]);
            const indexB = getFieldIndex(b[1]);
            return indexA - indexB;
        });
        sortedKeys = entries.map(([k]) => k);
    } else {
        // FORMATO LEGACY: Usar orden del formulario o por defecto
        sortedKeys = sortFieldKeys(Object.keys(fields), formDefinition, sectionName);
    }
    
    // Campos de metadatos que nunca deben mostrarse en el PDF
    const METADATA_FIELDS = ['index', 'type', 'name', 'order', 'required', 'title', 'subindex', 'parentIndex'];
    
    for (const key of sortedKeys) {
        const rawValue = fields[key];
        if (rawValue == null) continue;
        
        // Ignorar campos con clave vacía (firmas sin nombre)
        if (key === '' || key.trim() === '') continue;
        
        // Ignorar claves de firma (VS_0_, VS_1_, etc.)
        if (/^[A-Z]+_\d+_$/.test(key)) continue;
        
        // Ignorar campos de metadatos (index, type, name, etc.)
        if (METADATA_FIELDS.includes(key.toLowerCase())) continue;
        
        // Detectar si es el nuevo formato indexado
        const isIndexed = isIndexedField(rawValue);
        let value = extractFieldValue(rawValue);
        let fieldName = key;
        
        // Si es formato indexado, usar el name del objeto si existe
        if (isIndexed && rawValue.name) {
            fieldName = rawValue.name;
        }
        
        // Para campos condicionales (SOLO formato legacy), intentar obtener el nombre real del campo
        let humanKey;
        if (!useIndexedFormat && isConditionalKey(key) && formDefinition) {
            const parentKey = getParentKeyFromConditional(key);
            const condIndex = parseInt(key.match(/_cond_?(\d+)/)?.[1] || '0');
            const conditionalName = getConditionalFieldName(formDefinition, sectionName, parentKey, condIndex);
            humanKey = conditionalName || humanizeCondKey(fieldName);
        } else {
            humanKey = humanizeCondKey(fieldName);
        }
        
        if (!humanKey || humanKey.trim() === '') continue;
        
        // Si hay sectionName, usarlo como prefijo con el separador
        const label = sectionName 
            ? `${sectionName}${SECTION_SEPARATOR}${humanKey}`
            : (prefix ? `${prefix}${humanKey}` : humanKey);

        // Procesar campos anidados del formato indexado
        if (isIndexed && rawValue.nestedFields && typeof rawValue.nestedFields === 'object') {
            // Primero añadir el campo padre
            let formattedValue = value;
            if (typeof value === 'string') {
                if (rawValue.type === 'DATE_TIME' || key.toLowerCase().includes('hora')) {
                    formattedValue = formatTimeValue(value);
                } else if (rawValue.type === 'DATE') {
                    formattedValue = formatDateOnly(value);
                }
            }
            result.push({ label, value: String(formattedValue), section: sectionName || prefix, index: rawValue.index });
            
            // Luego procesar campos anidados ordenados por subindex
            const nestedEntries = Object.entries(rawValue.nestedFields);
            nestedEntries.sort((a, b) => {
                const subA = a[1]?.subindex || a[1]?.parentIndex || 0;
                const subB = b[1]?.subindex || b[1]?.parentIndex || 0;
                return String(subA).localeCompare(String(subB));
            });
            
            for (const [nestedKey, nestedValue] of nestedEntries) {
                if (nestedValue && typeof nestedValue === 'object') {
                    const nestedName = nestedValue.name || humanizeCondKey(nestedKey);
                    const nestedLabel = sectionName 
                        ? `${sectionName}${SECTION_SEPARATOR}${nestedName}`
                        : nestedName;
                    let nestedFormattedValue = nestedValue.value;
                    if (nestedValue.type === 'DATE_TIME') {
                        nestedFormattedValue = formatTimeValue(nestedValue.value);
                    }
                    result.push({ 
                        label: nestedLabel, 
                        value: String(nestedFormattedValue ?? ''), 
                        section: sectionName || prefix,
                        index: nestedValue.subindex || nestedValue.parentIndex
                    });
                }
            }
            continue;
        }

        if (isTimestampOrDate(value)) {
            try {
                const dateObj = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
                const str = dateObj.toLocaleDateString('es-ES', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                result.push({ label, value: str, section: sectionName || prefix });
            } catch {
                result.push({ label, value: String(value), section: sectionName || prefix });
            }
        } else if (typeof value === 'object' && !Array.isArray(value) && !isSpecialUrl(value)) {
            // Objeto anidado - aplanar directamente
            const nested = flattenFieldsForPdf(value, '', sectionName, formDefinition);
            result.push(...nested);
        } else if (Array.isArray(value)) {
            // Arrays de secciones repetibles (como VS = Vigilantes)
            // Cada elemento es un vigilante separado
            const sectionTitle = humanKey === 'VS' ? 'Vigilante' : humanKey;
            const expanded = value
                .map((v, i) => (v != null && typeof v === 'object' && !Array.isArray(v)
                    ? flattenFieldsForPdf(v, '', `${sectionTitle} ${i + 1}`, formDefinition)
                    : [{ label: `${sectionTitle} ${i + 1}`, value: v != null ? String(v) : '', section: `${sectionTitle} ${i + 1}` }]))
                .flat();
            result.push(...expanded);
        } else if (typeof value === 'string' && isSpecialUrl(value)) {
            continue;
        } else {
            // Formatear valores de hora/fecha (solo para formato indexado)
            let formattedValue = value;
            if (useIndexedFormat && typeof value === 'string') {
                const keyLower = key.toLowerCase();
                if (keyLower.includes('hora') || (isIndexed && rawValue.type === 'DATE_TIME')) {
                    formattedValue = formatTimeValue(value);
                } else if (isIndexed && rawValue.type === 'DATE') {
                    formattedValue = formatDateOnly(value);
                }
            }
            result.push({ label, value: String(formattedValue), section: sectionName || prefix });
        }
    }
    return result;
}

/**
 * Recursively collects all signature entries (label + URL) from document.fields for the PDF firmas section.
 * Supports nested/conditional form structure and arrays (e.g. VS[0].VS_0_).
 * @param {Object} fields - document.fields
 * @param {string} prefix - Label prefix for nested keys
 * @returns {Array<{ label: string, url: string }>}
 */
export function collectSignatureEntriesForPdf(fields, prefix = '') {
    if (!fields || typeof fields !== 'object') return [];
    const result = [];
    if (Array.isArray(fields)) {
        fields.forEach((item, index) => {
            if (item != null && typeof item === 'object') {
                const itemPrefix = prefix ? `${prefix} ${index + 1}` : `${index + 1}`;
                result.push(...collectSignatureEntriesForPdf(item, itemPrefix));
            }
        });
        return result;
    }
    for (const [key, value] of Object.entries(fields)) {
        const label = prefix ? `${prefix} - ${humanizeCondKey(key)}` : humanizeCondKey(key);
        if (typeof value === 'string' && (value.includes('signatures') || value.includes('signature'))) {
            result.push({ label, url: value });
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (item != null && typeof item === 'object' && !isTimestampOrDate(item)) {
                    const itemPrefix = prefix ? `${prefix} ${index + 1}` : `${label} ${index + 1}`;
                    result.push(...collectSignatureEntriesForPdf(item, itemPrefix));
                }
            });
        } else if (typeof value === 'object' && value !== null && !isSpecialUrl(value) && !isTimestampOrDate(value)) {
            result.push(...collectSignatureEntriesForPdf(value, `${label} - `));
        }
    }
    return result;
}

/**
 * Agrupa entradas aplanadas por sección.
 * Usa el campo 'section' si existe, o extrae del label antes del separador.
 * Devuelve [{ sectionName, entries }] para dibujar encabezados de sección en el PDF.
 */
function groupFlattenedBySection(flattened) {
    if (!flattened || flattened.length === 0) return [];
    const sections = [];
    let currentSection = null;
    for (const entry of flattened) {
        // Usar el campo section si existe, o extraer del label
        let sectionName = entry.section || '';
        if (!sectionName && entry.label.includes(SECTION_SEPARATOR)) {
            sectionName = entry.label.split(SECTION_SEPARATOR)[0].trim();
        } else if (!sectionName) {
            sectionName = entry.label;
        }
        if (!currentSection || currentSection.sectionName !== sectionName) {
            currentSection = { sectionName, entries: [] };
            sections.push(currentSection);
        }
        currentSection.entries.push(entry);
    }
    return sections;
}

/**
 * Agrupa entradas aplanadas según un diseño PDF (secciones con fieldLabels).
 * design.sections: [ { title, fieldLabels: string[] } ].
 * Devuelve [{ sectionName, entries }] en el orden del diseño; entradas no asignadas van a "Otros".
 */
function groupFlattenedByDesign(flattened, design) {
    if (!flattened || flattened.length === 0) return [];
    if (!design?.sections?.length) return groupFlattenedBySection(flattened);
    const assigned = new Set();
    const result = [];
    for (const sec of design.sections) {
        const title = (sec.title || '').trim() || 'Sin título';
        const labels = (sec.fieldLabels || []).map((l) => String(l).trim().toLowerCase()).filter(Boolean);
        const entries = flattened.filter((entry) => {
            if (assigned.has(entry)) return false;
            const entryLabel = (entry.label || '').toLowerCase();
            const firstSegment = entryLabel.includes(' > ') ? entryLabel.split(' > ')[0].trim() : entryLabel;
            const match = labels.some((l) => firstSegment === l || entryLabel.startsWith(l + ' > ') || entryLabel.includes(l));
            if (match) {
                assigned.add(entry);
                return true;
            }
            return false;
        });
        result.push({ sectionName: title, entries });
    }
    // Para formato legacy, incluir sección "Otros" con campos no asignados
    // Para formato indexado, no incluir (son metadatos internos)
    const rest = flattened.filter((e) => !assigned.has(e));
    if (rest.length > 0) {
        // Solo incluir "Otros" si NO es formato indexado (no tiene campos con index/value)
        const hasIndexed = rest.some(e => e.index !== undefined);
        if (!hasIndexed) {
            result.push({ sectionName: 'Otros', entries: rest });
        }
    }
    return result;
}

/** Convierte clave en etiqueta legible: quita _cond_5, _cond_nested_0, etc. Solo se muestra el nombre del campo (ej. Input 1). */
function humanizeCondKey(key) {
    const s = String(key);
    const withoutCond = s.replace(/_cond.*$/i, '').trim();
    const withoutTrailNum = withoutCond.replace(/_(\d+)_?$/, ' ($1)');
    return capitalizeFirstLetter(withoutTrailNum || s);
}


/**
 * Convierte un valor de campo (posiblemente objeto descriptor de formulario) en string para CSV.
 * Evita "[object Object]" cuando el campo es { value, name, type, nestedFields, ... }.
 * @param {*} value
 * @returns {string}
 */
function fieldValueToCsvString(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
        const parts = value.map((item) => {
            if (item != null && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value')) return fieldValueToCsvString(item.value);
            return fieldValueToCsvString(item);
        });
        return parts.filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) return fieldValueToCsvString(value.value);
        if (Array.isArray(value.nestedFields) && value.nestedFields.length > 0) {
            return value.nestedFields.map((item) => fieldValueToCsvString(item)).filter(Boolean).join(' · ');
        }
        const omitKeys = ['name', 'type', 'index', 'nestedFields', 'instanceCount'];
        const entries = Object.entries(value).filter(([k]) => !omitKeys.includes(k));
        if (entries.length === 0) return '';
        if (entries.length === 1) return fieldValueToCsvString(entries[0][1]);
        return entries.map(([, val]) => fieldValueToCsvString(val)).join(' · ');
    }
    return String(value);
}

/**
 * Escapa un string para celda CSV (comillas, saltos de línea).
 * @param {string} s
 * @returns {string}
 */
function escapeCsvCell(s) {
    const str = String(s);
    return str.replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, ' ');
}

/**
 * Construye el contenido CSV y metadatos para documentos (exportType === 'csv').
 * Optimizado para muchos registros: evita crear el ZIP cuando solo hay CSV.
 * @param {Array} documents - Documentos seleccionados.
 * @returns {{ csvContent: string, documentType: string } | null}
 */
function buildCSVContentAndMeta(documents) {
    if (!documents || documents.length === 0) return null;
    const documentType = documents[0].documentTypeId || 'documentos-personalizados';

    if (documentType === 'monumentos') {
        const header = [
            "id", "documentTypeId", "cliente", "tienda", "vigilante", "fecha",
            "Visitas", "Monumento", "Comunidad", "Pais"
        ];
        const csvRows = [];
        let idCounter = 1;
        for (const document of documents) {
            const rowData = [
                idCounter++,
                document.documentTypeId || '',
                document.cliente || '',
                document.tienda || '',
                document.vigilante || '',
                formatDateForFolder(document.fecha || '')
            ];
            const visitas = fieldValueToCsvString(document.fields?.Visitas ?? '');
            const monumento = fieldValueToCsvString(document.fields?.Monumento ?? '');
            const comunidad = fieldValueToCsvString(document.fields?.Comunidad ?? '').trim();
            const pais = fieldValueToCsvString(document.fields?.Pais ?? '').trim();
            rowData.push(`"${escapeCsvCell(visitas)}"`, `"${escapeCsvCell(monumento)}"`);
            if (comunidad !== '') {
                rowData.push(`"${escapeCsvCell(comunidad)}"`, '""');
            } else if (pais !== '') {
                rowData.push('""', `"${escapeCsvCell(pais)}"`);
            } else {
                rowData.push('""', '""');
            }
            csvRows.push(rowData.join(","));
        }
        const csvContent = [header.join(","), ...csvRows].join("\n");
        return { csvContent, documentType };
    }

    const allFields = new Set();
    documents.forEach(doc => {
        if (doc.fields) {
            Object.keys(doc.fields).forEach(field => {
                if (field !== 'fotos' && !field.includes('signature')) allFields.add(field);
            });
        }
    });
    const header = ["documentTypeId", "cliente", "tienda", "vigilante", "fecha", ...Array.from(allFields)];
    const csvRows = [];
    for (const document of documents) {
        const rowData = [
            document.documentTypeId || '',
            document.cliente || '',
            document.tienda || '',
            document.vigilante || '',
            formatDateForFolder(document.fecha || '')
        ];
        for (const field of Array.from(allFields)) {
            const value = document.fields?.[field];
            const stringValue = fieldValueToCsvString(value ?? '');
            rowData.push(`"${escapeCsvCell(stringValue)}"`);
        }
        csvRows.push(rowData.join(","));
    }
    const csvContent = [header.join(","), ...csvRows].join("\n");
    return { csvContent, documentType };
}

/**
 * Devuelve un Blob del CSV para descarga directa (sin ZIP). Útil para muchos documentos (ej. monumentos 500+).
 * @param {Array} documents - Documentos seleccionados.
 * @param {string} exportType - Debe ser 'csv'.
 * @returns {{ blob: Blob, filename: string } | null}
 */
export function getCSVBlobForDocuments(documents, exportType) {
    if (exportType !== 'csv' || !documents || documents.length === 0) return null;
    const built = buildCSVContentAndMeta(documents);
    if (!built) return null;
    const bom = "\uFEFF";
    const blob = new Blob([bom + built.csvContent], { type: 'text/csv;charset=utf-8' });
    return { blob, filename: `${built.documentType}.csv` };
}

/**
* Descarga los documentos seleccionados como archivos CSV dentro de una carpeta ZIP.
* @param {Array} documents - Documentos seleccionados.
* @param {Object} folder - Carpeta donde se guardarán los archivos en el ZIP.
* @param {string} exportType - Tipo de exportación ('csv' para formularios personalizados).
*/
export function downloadSelectedCSV(documents, folder, exportType) {
    // Para formularios personalizados con exportType === 'csv', procesar todos los documentos
    if (exportType === 'csv') {
        if (documents.length > 0) {
            const built = buildCSVContentAndMeta(documents);
            if (built) {
                const { csvContent, documentType } = built;
                const folderName = getFolderName(documentType);
                const csvFolder = folder.folder(folderName);
                csvFolder.file(`${documentType}.csv`, csvContent);
                console.log(`CSV generado para ${documents.length} documentos del tipo: ${documentType}`);
            }
        } else {
            console.log("No hay documentos para exportar como CSV.");
        }
    } else {
        // Lógica original para documentos tipo "112"
        const csvDocs = documents.filter((doc) => doc.documentTypeId === "112");
        if (csvDocs.length > 0) {
            // Agrupar los documentos 112 en uno solo
            const header = Object.keys(csvDocs[0].fields);
            header.unshift("fecha");
            header.unshift("vigilantes");
            header.unshift("tiendas");

            const csvRows = [];
            for (const document of csvDocs) {
                const keys = Object.keys(csvDocs[0].fields);
                const rowData = keys.map((key) => {
                    const value = document.fields[key];
                    return `"${escapeCsvCell(fieldValueToCsvString(value ?? ''))}"`;
                });
                const formattedDate = formatDateForFolder(document.fecha);
                rowData.unshift(`"${escapeCsvCell(formattedDate)}"`, `"${escapeCsvCell(document.vigilante)}"`, `"${escapeCsvCell(document.tienda)}"`);
                csvRows.push(rowData.join(","));
            }

            // Convertir el arreglo csvRows en una cadena separada por saltos de línea
            const csvContent = [header.join(","), ...csvRows].join("\n");

            const folderName = getFolderName(csvDocs[0].documentTypeId);
            folder = folder.folder(folderName);
            folder.file(`${folderName}.csv`, csvContent);
        } else {
            console.log("No hay documentos del tipo 112 seleccionados.");
        }
    }
}

/**
 * Verifica si una fecha está dentro de los últimos N días.
 * Versión mejorada que maneja zonas horarias adecuadamente.
 *
 * @param {string|Date} date - La fecha a comprobar en formato 'dd/MM/yyyy' o un objeto Date
 * @param {number} n - Número de días atrás para comparar
 * @returns {boolean} - True si la fecha está dentro del periodo, false de lo contrario
 */
export function isWithinLastNDays(date, n) {
    try {
        // Convertir a objeto Date si es string
        let targetDate;
        if (typeof date === 'string') {
            const parts = date.split('/');
            if (parts.length === 3) {
                // Formato dd/MM/yyyy
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // Meses en JS son 0-11
                const year = parseInt(parts[2], 10);
                targetDate = new Date(year, month, day);
            } else {
                // Intentar parsear como fecha normal
                targetDate = new Date(date);
            }
        } else {
            targetDate = date instanceof Date ? date : new Date(date);
        }

        // Validar que la fecha sea válida
        if (isNaN(targetDate.getTime())) {
            console.error("Fecha inválida:", date);
            return false;
        }

        // Normalizar a inicio del día para evitar problemas con horas
        targetDate.setHours(0, 0, 0, 0);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Calcular diferencia en días
        const differenceMs = today.getTime() - targetDate.getTime();
        const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
        
        return differenceDays <= n;
    } catch (error) {
        console.error("Error en isWithinLastNDays:", error);
        return false;
    }
}

/**
 * Formatea una fecha para mostrarla en formato dd/mm/yyyy.
 * Versión mejorada con mejor manejo de errores.
 *
 * @param {string|Date} dateInput - Fecha de entrada
 * @returns {string} - Fecha formateada o cadena vacía en caso de error
 */
export function formatDate(dateInput) {
    try {
      // Si no hay entrada, retornar cadena vacía
      if (!dateInput) return '';
      
      // Si ya es una cadena con formato dd/mm/yyyy, devolverla
      if (typeof dateInput === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
        return dateInput;
      }
      
      let date;
      
      // Manejar formato español de toLocaleString ("dd/mm/yyyy, hh:mm:ss")
      if (typeof dateInput === 'string' && dateInput.includes('/') && dateInput.includes(':')) {
        // Extraer solo la parte de la fecha (antes de la coma)
        const datePart = dateInput.split(',')[0];
        const parts = datePart.split('/');
        
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Meses en JS son 0-11
          const year = parseInt(parts[2], 10);
          date = new Date(year, month, day);
        } else {
          date = new Date(dateInput);
        }
      } else {
        // Otros formatos, intentar parseo directo
        date = new Date(dateInput);
      }
      
      // Validar fecha
      if (isNaN(date.getTime())) {
        return '';
      }
      
      // Formatear a dd/mm/yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error en formatDate:", error);
      return '';
    }
  }

/**
 * Aplica un filtro de fecha para determinar si un documento debe mostrarse según la fecha seleccionada.
 * Versión mejorada que funciona correctamente con todos los filtros.
 *
 * @param {Object} row - El documento/fila a filtrar
 * @param {string} documentDate - La fecha del documento en formato dd/MM/yyyy
 * @param {string} dateFilter - El tipo de filtro ('all', 'today', 'last3Days', 'lastWeek')
 * @returns {boolean} - True si el documento pasa el filtro, false en caso contrario
 */
export function applyDateFilter(row, documentDate, dateFilter) {
    // Si el filtro es "all", siempre retornar true
    if (dateFilter === "all") return true;
    
    try {
      // Implementación integrada de normalizeFecha
      const normalizarFecha = (dateInput) => {
        try {
          if (!dateInput) return null;
          
          let fecha;
          if (typeof dateInput === 'string') {
            // Detectar formato dd/mm/yyyy o d/m/yyyy
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
              const parts = dateInput.split('/');
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1; // Meses en JS son 0-11
              const year = parseInt(parts[2], 10);
              fecha = new Date(year, month, day);
            } else {
              // Intentar parsear directamente
              fecha = new Date(dateInput);
            }
          } else {
            fecha = new Date(dateInput);
          }
          
          // Validar fecha
          if (isNaN(fecha.getTime())) {
            return null;
          }
          
          // Normalizar a inicio del día
          fecha.setHours(0, 0, 0, 0);
          return fecha;
        } catch (error) {
          console.error("Error en normalización de fecha:", error);
          return null;
        }
      };
      
      // Normalizar la fecha del documento usando la implementación local
      const docDate = normalizarFecha(documentDate);
      if (!docDate) return false; // Si no se puede normalizar la fecha, excluir el documento
      
      switch (dateFilter) {
        case "today": {
          // Verificar si es hoy
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return docDate.getTime() === today.getTime();
        }
        
        case "last3Days": {
          // Verificar si está dentro de los últimos 3 días
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const differenceMs = today.getTime() - docDate.getTime();
          const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
          
          return differenceDays <= 2; // 0 = hoy, 1 = ayer, 2 = anteayer
        }
          
        case "lastWeek": {
          // Verificar si está dentro de la última semana
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const differenceMs = today.getTime() - docDate.getTime();
          const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
          
          return differenceDays <= 6; // 0-6 = últimos 7 días
        }
          
        default:
          return true;
      }
    } catch (error) {
      console.error("Error en applyDateFilter:", error);
      return true; // En caso de error, incluir el documento
    }
  }
  

/**
 * Formatea el tiempo en segundos a un formato legible (mm:ss o hh:mm:ss)
 * @param {number} seconds - Segundos a formatear
 * @returns {string} - Tiempo formateado
 */
export function formatTimeRemaining(seconds) {
    if (!seconds || seconds < 0 || !isFinite(seconds)) return '--:--';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Componente de modal para mostrar el estado de la descarga.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.isOpen - Indica si el modal está abierto.
 * @param {Function} props.onClose - Función para cerrar el modal.
 * @param {number} props.progress - Progreso de la descarga en porcentaje.
 * @param {string} props.feedback - Retroalimentación de la descarga.
 * @param {number} props.timeRemaining - Tiempo estimado restante en segundos.
 * @param {Object} props.downloadStats - Estadísticas de descarga (velocidad, procesados, total).
 * @returns {JSX.Element} - Elemento JSX que representa el modal.
 */
export const DownloadModal = ({ isOpen, onClose, progress, feedback, timeRemaining, downloadStats }) => (
    <div className={`download-modal ${isOpen ? "open" : ""}`}>
        <div className="modal-content">
            <h3>Estado de la descarga</h3>
            {progress !== 0 && progress < 100 && (
                <div className="download-progress-section">
                    <p className="download-status">Descargando...</p>
                    
                    {/* Barra de progreso visual */}
                    <div className="progress-bar-container">
                        <div 
                            className="progress-bar-fill" 
                            style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                    </div>
                    
                    <p className="progress-percentage">{progress.toFixed(1)}%</p>
                    
                    {/* Tiempo estimado restante */}
                    {timeRemaining !== undefined && timeRemaining > 0 && (
                        <div className="time-remaining">
                            <span className="time-icon">⏱️</span>
                            <span className="time-label">Tiempo restante: </span>
                            <span className="time-value">{formatTimeRemaining(timeRemaining)}</span>
                        </div>
                    )}
                    
                    {/* Estadísticas de descarga */}
                    {downloadStats && (
                        <div className="download-stats">
                            {downloadStats.processed !== undefined && downloadStats.total !== undefined && (
                                <p className="stats-item">
                                    📄 Documentos: {downloadStats.processed} / {downloadStats.total}
                                </p>
                            )}
                            {downloadStats.speed !== undefined && downloadStats.speed > 0 && (
                                <p className="stats-item">
                                    🚀 Velocidad: {downloadStats.speed.toFixed(1)} docs/seg
                                </p>
                            )}
                            {downloadStats.currentPhase && (
                                <p className="stats-item phase">
                                    📋 {downloadStats.currentPhase}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}
            {progress === 100 && (
                <div className="download-complete">
                    <span className="complete-icon">✅</span>
                    <p>Descarga completada</p>
                </div>
            )}
            {feedback && <p className="feedback-message">{feedback}</p>}
            <button onClick={onClose}>Aceptar</button>
        </div>
    </div>
);

/**
 * Convierte una fecha de varios formatos posibles a un objeto Date normalizado.
 * Mejora la compatibilidad con diferentes formatos de entrada.
 *
 * @param {string|Date} dateInput - Fecha de entrada en formato string o Date
 * @returns {Date} - Objeto Date normalizado
 */
export function normalizeFecha(dateInput) {
    try {
        if (!dateInput) return null;
        
        let fecha;
        if (typeof dateInput === 'string') {
            // Detectar formato dd/mm/yyyy o d/m/yyyy
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateInput)) {
                const parts = dateInput.split('/');
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // Meses en JS son 0-11
                const year = parseInt(parts[2], 10);
                fecha = new Date(year, month, day);
            } else {
                // Intentar parsear directamente
                fecha = new Date(dateInput);
            }
        } else {
            fecha = new Date(dateInput);
        }
        
        // Validar fecha
        if (isNaN(fecha.getTime())) {
            console.error("Fecha inválida en normalizeFecha:", dateInput);
            return null;
        }
        
        // Normalizar a inicio del día
        fecha.setHours(0, 0, 0, 0);
        return fecha;
    } catch (error) {
        console.error("Error en normalizeFecha:", error);
        return null;
    }
}